import { useRef } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useGameStore } from "@/store/gameStore";
import { Game } from "@/store/types";
import { gameDocToGame, gameToCreateInput } from "@/hooks/convex/gameMapping";

export function useConvexEnableSync() {
  const setGame = useGameStore((state) => state.setGame);
  const setSyncStatus = useGameStore((state) => state.setSyncStatus);

  const hasInitiatedSync = useRef<boolean>(false);
  const convexSyncInProgress = useRef<boolean>(false);

  const createGameMutation = useMutation(api.game.create);

  const enableSync = async (): Promise<Game | null> => {
    if (convexSyncInProgress.current || hasInitiatedSync.current) return null;
    const currentGame = useGameStore.getState().game;
    if (currentGame.slug) return currentGame;

    try {
      convexSyncInProgress.current = true;
      setSyncStatus("syncing");
      const requestedGame = useGameStore.getState().game;

      const createdGame = await createGameMutation({
        game: gameToCreateInput(requestedGame),
      });

      if (!createdGame || !createdGame.slug) {
        setSyncStatus("failed");
        return null;
      }

      const latestGame = useGameStore.getState().game;
      if (latestGame.slug || latestGame.updatedAt !== requestedGame.updatedAt) {
        if (!latestGame.slug) setSyncStatus("failed");
        return null;
      }

      const updatedGame: Game = gameDocToGame(createdGame.game);

      setGame(updatedGame, { preserveLocalHistory: true });
      hasInitiatedSync.current = true;

      return updatedGame;
    } catch (error) {
      console.error("Sync error:", error);
      setSyncStatus("failed");
      return null;
    } finally {
      convexSyncInProgress.current = false;
    }
  };

  // Moves this device's game to a new share code, keeping its progress.
  // Players on the old code stay on the old game.
  const reshare = async (): Promise<Game | null> => {
    if (convexSyncInProgress.current) return null;
    useGameStore.getState().unShareGame();
    hasInitiatedSync.current = false;
    return enableSync();
  };

  return { enableSync, reshare };
}
